import { t } from 'i18next';
import { useState, useEffect } from 'react';
import { applicationStages } from '../../utils/enums';

import './Status.scss';

/**Localized status text and icon
 * @param {object} props Incoming JSX attributes.
 * @param {string} [props.className] A string value that should be a class string delimited by spaces for the purposes of overriding style and functionality in parent components.
 * @param {number} props.status An integer value that corresponds to a status enumeration.
 * @returns {JSX.Element} Localized status text and icon
 * @see {@link https://bitbucket.org/pcgeducation/shipyard/src/master/shipyard-web/src/components/shared/Status.md Status Documentation} for further information
 */

export default function Status(props) {
    const [circleColor, setCircleColor] = useState(() => {
        //TODO #TechnicalDebt - Remove the ability to control the color and text of this component via integer status prop.
        if (typeof props?.status === 'number') {
            let _circleColor;
            switch (applicationStages[props.status]) {
                case 'AppStages.Started':
                case 'AppStages.InitialReview':
                    _circleColor = 'lightblue';
                    break;
                case 'AppStages.FinalReview':
                    _circleColor = 'blue';
                    break;
                case 'AppStages.Returned':
                    _circleColor = 'orange';
                    break;
                case 'AppStages.DHSReview':
                    _circleColor = 'magenta';
                    break;
                case 'AppStages.Withdrawn':
                    _circleColor = 'lightpurple';
                    break;
                case 'AppStages.SchoolReview':
                    _circleColor = 'purple';
                    break;
                case 'AppStages.Approved':
                    _circleColor = 'darkblue';
                    break;
                case 'AppStages.Declined':
                    _circleColor = 'yellow';
                    break;
                case 'AppStages.Pending':
                    _circleColor = 'lightgreen';
                    break;
                case 'AppStages.Accepted':
                    _circleColor = 'green';
                    break;
                case 'AppStages.EnrollmentChange':
                    _circleColor = 'navyblue';
                    break;
                case 'AppStages.Denied':
                    _circleColor = 'red';
                    break;
                default:
                    _circleColor = 'red';
                    break;
            }
            return _circleColor;
        } else if (
            props?.status instanceof Object &&
            Number.isInteger(props?.status?.id) &&
            props?.status?.description instanceof String &&
            props?.status?.displayColor instanceof String
        ) {
            return props?.status?.displayColor;
        } else {
            return 'red';
        }
    });

    useEffect(() => {
        setCircleColor((prev) => {
            if (typeof props?.status === 'number') {
                let _circleColor;
                switch (applicationStages[props.status]) {
                    case 'AppStages.Started':
                    case 'AppStages.InitialReview':
                        _circleColor = 'lightblue';
                        break;
                    case 'AppStages.FinalReview':
                        _circleColor = 'blue';
                        break;
                    case 'AppStages.Returned':
                        _circleColor = 'orange';
                        break;
                    case 'AppStages.DHSReview':
                        _circleColor = 'magenta';
                        break;
                    case 'AppStages.Withdrawn':
                        _circleColor = 'lightpurple';
                        break;
                    case 'AppStages.SchoolReview':
                        _circleColor = 'purple';
                        break;
                    case 'AppStages.Approved':
                        _circleColor = 'darkblue';
                        break;
                    case 'AppStages.Declined':
                        _circleColor = 'yellow';
                        break;
                    case 'AppStages.Pending':
                        _circleColor = 'lightgreen';
                        break;
                    case 'AppStages.Accepted':
                        _circleColor = 'green';
                        break;
                    case 'AppStages.EnrollmentChange':
                        _circleColor = 'navyblue';
                        break;
                    case 'AppStages.Denied':
                        _circleColor = 'red';
                        break;
                    default:
                        _circleColor = 'red';
                        break;
                }
                return _circleColor;
            } else if (
                props?.status instanceof Object &&
                Number.isInteger(props?.status?.id) &&
                typeof props?.status?.description === 'string' &&
                typeof props?.status?.displayColor === 'string'
            ) {
                return props?.status?.displayColor;
            } else {
                return prev;
            }
        });
    }, [props.status]);

    return (
        <span className={props?.className ? props.className + ' status-component' : 'status-component'}>
            <Circle circleColor={circleColor} />
            &nbsp;{typeof props?.status?.description === 'string' ? props?.status?.description : t(applicationStages[props.status])}
        </span>
    );
}

const Circle = ({ circleColor }) => {
    return (
        <span
            className={circleColor + ' circle'}
            style={{ backgroundColor: circleColor.includes('#') ? circleColor : null }}
        />
    );
};

//TODO Tucker - Rename this component more properly to something such as ApplicationInstanceStatus.
//TODO #TechnicalDebt - Description is not localized and will need to be.
